import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import tonService from '../services/tonService';

const TONBalance = ({ address }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBalance = async () => {
    if (!address) return;

    setLoading(true);
    try {
      const data = await tonService.getBalance(address);
      setBalance(data);
    } catch (error) {
      toast.error('Failed to fetch balance', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchBalance, 10000);
    return () => clearInterval(interval);
  }, [address]);

  if (!address) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            TON Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Connect wallet to view balance</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ton-balance-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            TON Balance
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchBalance}
            disabled={loading}
            data-testid="refresh-balance-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <CardDescription className="font-mono text-xs break-all">
          {address}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {balance ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Balance (TON)</p>
                <p className="text-2xl font-bold" data-testid="balance-ton">
                  {balance.balance_ton?.toFixed(4) || '0.0000'}
                </p>
                <p className="text-xs text-gray-500">TON</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">Balance (nano)</p>
                <p className="text-lg font-mono" data-testid="balance-nano">
                  {balance.balance_nano || '0'}
                </p>
                <p className="text-xs text-gray-500">nanotons</p>
              </div>
            </div>

            <div className="pt-3 border-t">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            {loading ? (
              <p className="text-sm text-gray-500">Loading balance...</p>
            ) : (
              <p className="text-sm text-gray-500">No balance data</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TONBalance;
